import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Schedule as PendingIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiServices } from '../../../services/apiServices';
import Swal from 'sweetalert2';

const AffiliateDetails = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [viewMoreOpen, setViewMoreOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [customerDetails, setCustomerDetails] = useState([]);
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState(false);
  const [customerDetailsError, setCustomerDetailsError] = useState('');
  const [aggregateData, setAggregateData] = useState(null);

  const fetchAffiliateDetails = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiServices.getAPI('getAffiliateDetails');

      if (response.data && response.data.success) {
        setAffiliates(response.data.affiliates || []);
      } else {
        setError('Failed to fetch affiliate details');
      }
    } catch (err) {
      console.error('Error fetching affiliate details:', err);
      setError('Error fetching affiliate details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateDetails();
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const handleViewMore = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setViewMoreOpen(true);
    setActiveTab(0); // Reset to first tab
    setCustomerDetails([]); // Clear previous customer details
  };

  const handleCloseViewMore = () => {
    setViewMoreOpen(false);
    setSelectedAffiliate(null);
    setActiveTab(0);
    setCustomerDetails([]);
    setCustomerDetailsError('');
    setAggregateData(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Fetch customer details when switching to customer details tab
    if (newValue === 1 && selectedAffiliate && customerDetails.length === 0) {
      fetchCustomerDetails();
    }
  };

  const fetchCustomerDetails = async () => {
    if (!selectedAffiliate) return;

    setCustomerDetailsLoading(true);
    setCustomerDetailsError('');

    try {
      const payload = {
        affiliate_id: selectedAffiliate.id
      };

      const response = await apiServices.postAPI('affiliateSalesDetails', payload);

      if (response.data) {
        // Handle different response structures
        if (response.data.success) {
          // Handle orders response structure
          if (response.data.orders && Array.isArray(response.data.orders)) {
            setCustomerDetails(response.data.orders);
            setAggregateData(response.data.aggregate || null);
          } else {
            setCustomerDetails(response.data.data || response.data.sales || response.data.customers || []);
            setAggregateData(null);
          }
        } else if (Array.isArray(response.data)) {
          setCustomerDetails(response.data);
          setAggregateData(null);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          setCustomerDetails(response.data.data);
          setAggregateData(null);
        } else {
          setCustomerDetailsError(response.data.message || 'No customer details found');
        }
      } else {
        setCustomerDetailsError('Failed to fetch customer details');
      }
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setCustomerDetailsError(err.response?.data?.message || 'Error fetching customer details. Please try again.');
    } finally {
      setCustomerDetailsLoading(false);
    }
  };

  // Filter affiliates based on search term
  const filteredAffiliates = affiliates.filter((affiliate) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      affiliate.name?.toLowerCase().includes(searchLower) ||
      affiliate.email?.toLowerCase().includes(searchLower) ||
      affiliate.ref_code?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const paginatedAffiliates = filteredAffiliates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate totals
  const totalReferralEarnings = affiliates.reduce((sum, aff) => sum + (aff.total_referral_earnings || 0), 0);
  const totalNetworkEarnings = affiliates.reduce((sum, aff) => sum + (aff.total_network_earnings || 0), 0);
  const totalAmountPaid = affiliates.reduce((sum, aff) => sum + (aff.total_amount_paid || 0), 0);
  const totalAmountPending = affiliates.reduce((sum, aff) => sum + (aff.total_amount_pending || 0), 0);
  const totalOrders = affiliates.reduce((sum, aff) => sum + (aff.number_of_orders || 0), 0);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" className="green-title" sx={{ fontWeight: 600 }}>
          Gifts
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchAffiliateDetails} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3, display: 'none' }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <WalletIcon sx={{ fontSize: 30, color: '#1976d2', mb: 1 }} />
            <Typography variant="h6" color="primary">
              {formatCurrency(totalReferralEarnings)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Referral Earnings
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
            <TrendingUpIcon sx={{ fontSize: 30, color: '#7b1fa2', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#7b1fa2' }}>
              {formatCurrency(totalNetworkEarnings)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Network Earnings
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
            <PaymentIcon sx={{ fontSize: 30, color: '#388e3c', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#388e3c' }}>
              {formatCurrency(totalAmountPaid)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Amount Paid
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <PendingIcon sx={{ fontSize: 30, color: '#f57c00', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#f57c00' }}>
              {formatCurrency(totalAmountPending)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Pending
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
            <TrendingUpIcon sx={{ fontSize: 30, color: '#c2185b', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#c2185b' }}>
              {totalOrders}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Orders
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, or referral code..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress sx={{ color: '#3A8139' }} />
        </Box>
      )}

      {/* Affiliates Table */}
      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Referral Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAffiliates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No affiliates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAffiliates.map((affiliate) => (
                  <TableRow key={affiliate.id} hover>
                    <TableCell>{affiliate.id}</TableCell>
                    <TableCell>{affiliate.name || '-'}</TableCell>
                    <TableCell>{affiliate.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={affiliate.ref_code || '-'}
                        size="small"
                        sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewMore(affiliate)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#3A8139',
                          color: '#3A8139',
                          '&:hover': {
                            borderColor: '#2d6329',
                            backgroundColor: 'rgba(58, 129, 57, 0.04)',
                          },
                        }}
                      >
                        View More
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredAffiliates.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* View More Dialog */}
      <Dialog
        open={viewMoreOpen}
        onClose={handleCloseViewMore}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" className="green-title" sx={{ fontWeight: 600 }}>
            Gifts
          </Typography>
          <IconButton onClick={handleCloseViewMore} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAffiliate && (
            <Box>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="affiliate details tabs"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      minHeight: 48,
                    },
                  }}
                >
                  <Tab label="Affiliate Info" />
                  <Tab label="Customer Details" />
                </Tabs>
              </Box>

              {/* Tab Content */}
              {activeTab === 0 && (
                <Grid container spacing={3}>
                

                {/* Orders Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#214F7D', fontWeight: 600 }}>
                    Orders
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Number of Orders
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.2rem', color: '#c2185b' }}>
                        {selectedAffiliate.number_of_orders || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {/* Earnings Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#214F7D', fontWeight: 600 }}>
                    Earnings & Revenue
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Referral Earnings
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
                        {formatCurrency(selectedAffiliate.total_referral_earnings)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#388e3c' }}>
                        {formatCurrency(selectedAffiliate.total_amount_paid)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Pending
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#f57c00' }}>
                        {formatCurrency(selectedAffiliate.total_amount_pending)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Subtotal Revenue
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatCurrency(selectedAffiliate.subtotal_revenue)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>


                {/* Commission & Status */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#214F7D', fontWeight: 600 }}>
                    Commission
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Commission
                      </Typography>
                      {selectedAffiliate.commission ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selectedAffiliate.commission.amount}
                            {selectedAffiliate.commission.type === 'percentage' ? '%' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            on {selectedAffiliate.commission.on}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          -
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              )}

              {/* Customer Details Tab */}
              {activeTab === 1 && (
                <Box>
                  {customerDetailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <CircularProgress sx={{ color: '#3A8139' }} />
                    </Box>
                  ) : customerDetailsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {customerDetailsError}
                    </Alert>
                  ) : customerDetails.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No orders found
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* Aggregate Statistics */}
                      {aggregateData && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                              <Typography variant="h6" color="primary">
                                {aggregateData.order_count || 0}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Total Orders
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                              <Typography variant="h6" sx={{ color: '#388e3c' }}>
                                {formatCurrency(parseFloat(aggregateData.order_total || 0))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Order Total
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                              <Typography variant="h6" sx={{ color: '#f57c00' }}>
                                {formatCurrency(parseFloat(aggregateData.order_subtotal || 0))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Order Subtotal
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                              <Typography variant="h6" sx={{ color: '#7b1fa2' }}>
                                {formatCurrency(parseFloat(aggregateData.affiliate_commission || 0))}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Total Commission
                              </Typography>
                            </Card>
                          </Grid>
                        </Grid>
                      )}

                      {/* Orders List */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {customerDetails.map((order, index) => (
                          <Card key={order.id || index} sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {order.number || `Order #${order.id}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {order.created ? new Date(order.created).toLocaleString() : '-'}
                              </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Order Summary */}
                            <Grid container spacing={2} sx={{ mb: 2, justifyContent: 'center' }}>
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd', height: '70%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80px' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Total
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                    {formatCurrency(parseFloat(order.total || 0))}
                                  </Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8', height: '70%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80px' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Subtotal
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#388e3c' }}>
                                    {formatCurrency(parseFloat(order.subtotal || 0))}
                                  </Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5', height: '70%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80px' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Commission
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                                    {formatCurrency(parseFloat(order.commission || 0))}
                                  </Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0', height: '70%', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80px' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Payment Status
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Chip
                                      label={order.is_paid ? 'Paid' : 'Unpaid'}
                                      color={order.is_paid ? 'success' : 'warning'}
                                      size="small"
                                    />
                                  </Box>
                                </Card>
                              </Grid>
                            </Grid>

                            {/* Customer Information */}
                            {order.customer && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Customer Information
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Name</Typography>
                                    <Typography variant="body2">{order.customer.name || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Email</Typography>
                                    <Typography variant="body2">{order.customer.email || '-'}</Typography>
                                  </Grid>
                                  {order.customer.phone && (
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                                      <Typography variant="body2">{order.customer.phone}</Typography>
                                    </Grid>
                                  )}
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Orders Count</Typography>
                                    <Typography variant="body2">{order.customer.orders_count || 0}</Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">New Customer</Typography>
                                    <Chip
                                      label={order.customer.is_new_customer ? 'Yes' : 'No'}
                                      color={order.customer.is_new_customer ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            )}

                            {/* Line Items */}
                            {order.line_items && order.line_items.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Order Items
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Quantity</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Price</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Commission</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {order.line_items.map((item, itemIndex) => (
                                        <TableRow key={item.id || itemIndex}>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {item.name || '-'}
                                            </Typography>
                                            
                                            {item.sku && (
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                SKU: {item.sku}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="right">{item.quantity || 0}</TableCell>
                                          <TableCell align="right">{formatCurrency(parseFloat(item.price || 0))}</TableCell>
                                          <TableCell align="right">{formatCurrency(parseFloat(item.total || 0))}</TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 500 }}>
                                              {formatCurrency(parseFloat(item.commission || 0))}
                                            </Typography>
                                            {item.commission_type && (
                                              <Typography variant="caption" color="text.secondary">
                                                ({item.commission_value}%)
                                              </Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewMore} variant="contained" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AffiliateDetails;
